import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { showToast } from 'Core/redux/toast/slice';
import { closeModal, showModal  } from 'Core/redux/main/slice';
import { redux } from 'Core/redux';
import { saveAs } from 'Core/utils';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { selectStatusLoading } from '../../../core/redux/status/selectors';
import { LocationService } from '../../../service/api/location-service';
const { LOCATION_ADD_EDIT } = MODAL_TYPES;

export const STORE_KEY = 'locations';
export const INITIAL_STATE = {
  data: [],  
  loading: false,
  error: null,
  location: null,
  printersdata: [],
  filters: {
    status: {
      value: 'In Location', 
      label: 'In Location' 
    },
    searchText: ''
  },  
  filterVisible: false,
  tileView: true,
  selectedLocation: null,
  editModalVisible: false,  
  
};

export const fetchLocations = createAsyncThunk(
  'locations/fetchLocations',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await LocationService.getAllLocations(filters);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Locations');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Locations');
    }
  },
);

export const addLocation = createAsyncThunk(
  'locations/addLocation',
  async (locationPayload, { rejectWithValue }) => {
    try {
      const response = await LocationService.saveLocation(locationPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add Locations');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add Locations');
    }
  },
);

export const editLocation = createAsyncThunk(
  'locations/editLocation',
  async (locationPayload, { rejectWithValue }) => {
    try {
      const response = await LocationService.saveLocation(locationPayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update location');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update location');
    }
  },
);

export const downloadLocations = createAsyncThunk(
  'locations/downloadLocations',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await LocationService.downloadLocation(filters);
      const blob = response;
      saveAs(blob, `Locations.xlsx`);
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to download Locations');
    }
  },
);

export const deleteLocation = createAsyncThunk(
  'locations/deleteLocation',
  async (locationId, { rejectWithValue }) => {
    try {
      const response = await LocationService.deleteLocation(locationId);
      if (response.status === 200) {
        return locationId;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to delete location');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete location');
    }
  },
);

export const fetchLocationsWithFilters = () => (dispatch, getState) => {
  const filters = getState()[STORE_KEY].filters;
  dispatch(
    fetchLocations({
      searchText: filters.searchText,
      name:"",
      description:""
    }),
  );
};

export const downloadLocationsWithFilters = () => (dispatch, getState) => {
  const filters = getState()[STORE_KEY].filters;
  dispatch(
    downloadLocations({
      searchText: filters.searchText,
      location: filters?.location?.value,
      status: filters?.status?.value,
      locationDate: filters?.shipDate,
    }),
  );
};

export const { reducer, actions } = createSlice({
  name: 'locations',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setLocation:(state, action) => {
      state.location = action.payload;
    },
    resetFilters: (state) => {
      state.filters = INITIAL_STATE.filters;
    },
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setLocation: (state, action) => {
      state.filters.location = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setStartDate: (state, action) => {
      state.filters.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.filters.endDate = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload; // Add reducer to update tileView
    },     
  },
  extraReducers: builder => {
    builder
      .addCase(fetchLocations.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addLocation.fulfilled, (state, action) => {
        const newLocation = {
          ...action.payload,
          isNewlyCreated: true,
        };
        state.data = [newLocation, ...state.data];
      })
      .addCase(addLocation.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(editLocation.fulfilled, (state, action) => {
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editLocation.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteLocation.fulfilled, (state, action) => {
        const newData = state.data.filter(s => s.id !== action.payload);
        state.data = newData;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addLocation.rejected, editLocation.rejected
  ),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addLocation.fulfilled, editLocation.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(LOCATION_ADD_EDIT));
    listenerApi.dispatch(fetchLocationsWithFilters());
    listenerApi.dispatch(
      showToast({
        message: 'Location saved successfully',
        type: 'success',
      }),
    );
  },
});


redux.listener.startListening({
  matcher: isAnyOf(editLocation.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(LOCATION_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Location updated successfully',
        type: 'success',
      }),
    );
  },
});
redux.listener.startListening({
  matcher: isAnyOf(addLocation.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(closeModal(LOCATION_ADD_EDIT));
    listenerApi.dispatch(
      showToast({
        message: 'Location saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(deleteLocation.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Location deleted successfully',
        type: 'success',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);
